// apps/vendor/src/pages/settings/Setup2FAPage.tsx
//
// Phase 1 Task 49 — 2FA enrollment page.
// Method picker (TOTP / Email OTP) → enroll → verify code → recovery codes.
//
// Endpoints (API-CONTRACTS L974+ / L1087+):
//   POST /auth/2fa/setup-totp     → { enrollmentToken, secret, otpAuthUri, qrCodeDataUrl }
//   POST /auth/2fa/verify-totp    → { recoveryCodes[8] }
//   POST /auth/2fa/setup-email    → { enrollmentToken, email, otpExpiresAt }
//   POST /auth/2fa/verify-email   → { recoveryCodes[8] }
//
// All four are Tier A sensitive — `requireFreshPerms({ window: '5m' })`.
// Disable button (also sensitive — handle perm-stale + impersonate-denylist
// 403 per CONTEXT.md / D-R7-08): POST /auth/2fa/disable.
//
// Goal coverage:.

import { useState } from 'react';
import type { FormEvent, JSX } from 'react';
import { useTranslation } from 'react-i18next';

import { apiClient } from '@printway/shared/api';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Form,
  FormControl,
  FormField,
  FormLabel,
  FormMessage,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  useAuth,
  SettingsLayout,
} from '@printway/ui';

type Method = 'TOTP' | 'EMAIL';
type Stage = 'pick' | 'enroll' | 'verify' | 'recovery' | 'disabled';

interface EnrollData {
  method: Method;
  enrollmentToken: string;
  // TOTP only
  qrCodeDataUrl?: string;
  otpAuthUri?: string;
  secret?: string;
  // EMAIL only
  email?: string;
}

type LooseClient = {
  POST: (p: string, init?: { body?: unknown }) => Promise<{
    data?: unknown;
    error?: unknown;
    response: Response;
  }>;
};

const FRESH_ERROR_CODES = new Set([
  'AUTH_FRESH_PERMS_REQUIRED',
  'AUTH_PERM_VERSION_STALE',
]);

async function readErrorCode(response: Response): Promise<string> {
  try {
    const env = (await response.clone().json()) as
      | { error?: { code?: string } }
      | undefined;
    if (env?.error?.code) return env.error.code;
  } catch {
    // ignore
  }
  return `HTTP_${response.status}`;
}

export function Setup2FAPage(): JSX.Element {
  const { t } = useTranslation('settings');
  const refresh = useAuth((s) => s.refresh);

  const [method, setMethod] = useState<Method>('TOTP');
  const [stage, setStage] = useState<Stage>('pick');
  const [enroll, setEnroll] = useState<EnrollData | null>(null);
  const [code, setCode] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // -------------------------------------------------------------------------
  // POST helpers — wrapped to retry once on AUTH_FRESH_PERMS_REQUIRED.
  // -------------------------------------------------------------------------
  const postWithFreshRetry = async (
    path: string,
    body: Record<string, unknown>,
  ): Promise<{ ok: boolean; data: unknown; status: number; code?: string }> => {
    const client = apiClient() as unknown as LooseClient;
    let { data, response } = await client.POST(path, { body });
    if (!response.ok && (response.status === 401 || response.status === 428)) {
      const code = await readErrorCode(response);
      if (FRESH_ERROR_CODES.has(code) || response.status === 428) {
        await refresh();
        ({ data, response } = await client.POST(path, { body }));
      }
    }
    if (!response.ok) {
      const code = await readErrorCode(response);
      return { ok: false, data, status: response.status, code };
    }
    return { ok: true, data, status: response.status };
  };

  // -------------------------------------------------------------------------
  // Stage 1 — start enrollment
  // -------------------------------------------------------------------------
  const handleStartEnroll = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      const path =
        method === 'TOTP'
          ? '/api/v1/auth/2fa/setup-totp'
          : '/api/v1/auth/2fa/setup-email';
      const body =
        method === 'TOTP' ? { label: 'PrintwayV3 (uvendor)' } : {};
      const res = await postWithFreshRetry(path, body);
      if (!res.ok) {
        // Impersonate-denylist 403 per D-R7-08: 2fa setup is on the denylist —
        // surface explicit message instead of generic.
        if (res.status === 403 && res.code === 'AUTH_IMPERSONATE_DENIED') {
          setError(t('twofa.errorImpersonateSetup'));
        } else if (
          res.status === 409 ||
          res.code === 'AUTH_2FA_ALREADY_ENABLED'
        ) {
          // 2FA already enrolled — the BE guards re-enrollment (setupEmail/
          // setupTotp → 409). Show a clear, actionable message instead of the
          // raw HTTP_409 (dbg #2256); the "Disable 2FA" button is right here.
          setError(t('twofa.errorAlreadyEnabled'));
        } else {
          setError(res.code ?? `HTTP_${res.status}`);
        }
        return;
      }
      const env = res.data as { data?: Record<string, unknown> } | undefined;
      const payload = env?.data ?? {};
      const enrollmentToken = payload['enrollmentToken'];
      if (typeof enrollmentToken !== 'string') {
        setError('MALFORMED_RESPONSE');
        return;
      }
      const next: EnrollData = { method, enrollmentToken };
      if (method === 'TOTP') {
        next.qrCodeDataUrl = payload['qrCodeDataUrl'] as string | undefined;
        next.otpAuthUri = payload['otpAuthUri'] as string | undefined;
        next.secret = payload['secret'] as string | undefined;
      } else {
        next.email = payload['email'] as string | undefined;
      }
      setEnroll(next);
      setStage('enroll');
    } catch (err) {
      setError(err instanceof Error ? err.message : '2FA_SETUP_FAILED');
    } finally {
      setBusy(false);
    }
  };

  // -------------------------------------------------------------------------
  // Stage 2 — verify code → receive recovery codes
  // -------------------------------------------------------------------------
  const handleVerify = async (
    ev: FormEvent<HTMLFormElement>,
  ): Promise<void> => {
    ev.preventDefault();
    if (!enroll) return;
    setBusy(true);
    setError(null);
    try {
      const path =
        enroll.method === 'TOTP'
          ? '/api/v1/auth/2fa/verify-totp'
          : '/api/v1/auth/2fa/verify-email';
      const res = await postWithFreshRetry(path, {
        enrollmentToken: enroll.enrollmentToken,
        code,
      });
      if (!res.ok) {
        setError(res.code ?? `HTTP_${res.status}`);
        return;
      }
      const env = res.data as { data?: { recoveryCodes?: string[] } } | undefined;
      const codes = env?.data?.recoveryCodes;
      if (Array.isArray(codes)) setRecoveryCodes(codes);
      setStage('recovery');
    } catch (err) {
      setError(err instanceof Error ? err.message : '2FA_VERIFY_FAILED');
    } finally {
      setBusy(false);
    }
  };

  // -------------------------------------------------------------------------
  // Disable 2FA — sensitive, perm-stale + denylist handling.
  // UAT #2259 — BE requires password (or current 2FA code) in the body;
  // the old empty {} body 400'd VALIDATION_FAILED with no usable FE
  // message → "không tắt được". Prompt for the account password first.
  // -------------------------------------------------------------------------
  const handleDisable = async (): Promise<void> => {
    const password = window.prompt(t('twofa.disablePasswordPrompt'));
    if (!password) return;
    setBusy(true);
    setError(null);
    try {
      const res = await postWithFreshRetry(
        '/api/v1/auth/2fa/disable',
        { password },
      );
      if (!res.ok) {
        if (res.status === 403 && res.code === 'AUTH_IMPERSONATE_DENIED') {
          setError(t('twofa.errorImpersonateDisable'));
        } else if (res.status === 401) {
          setError(t('twofa.errorPasswordWrong'));
        } else {
          setError(res.code ?? `HTTP_${res.status}`);
        }
        return;
      }
      setStage('disabled');
    } catch (err) {
      setError(err instanceof Error ? err.message : '2FA_DISABLE_FAILED');
    } finally {
      setBusy(false);
    }
  };

  return (
    <SettingsLayout portal="vendor" activeTab="security">
      <Card>
        <CardHeader>
          <CardTitle>{t('twofa.cardTitle')}</CardTitle>
          <CardDescription>
            {t('twofa.cardDescription')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {stage === 'pick' ? (
            <div className="space-y-4">
              <FormField>
                <FormLabel htmlFor="method">{t('twofa.methodLabel')}</FormLabel>
                <FormControl>
                  <Select
                    value={method}
                    onValueChange={(value) => setMethod(value as Method)}
                  >
                    <SelectTrigger id="method" data-testid="2fa-method-picker">
                      <SelectValue placeholder={t('twofa.methodLabel')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="TOTP">{t('twofa.methodTotp')}</SelectItem>
                      <SelectItem value="EMAIL">{t('twofa.methodEmail')}</SelectItem>
                    </SelectContent>
                  </Select>
                </FormControl>
              </FormField>
              {error ? (
                <FormMessage data-testid="2fa-error">{error}</FormMessage>
              ) : null}
              <div className="flex justify-between gap-2">
                <Button
                  variant="destructive"
                  onClick={() => void handleDisable()}
                  disabled={busy}
                  data-testid="2fa-disable"
                >
                  {busy ? t('twofa.processing') : t('twofa.disable')}
                </Button>
                <Button
                  onClick={() => void handleStartEnroll()}
                  disabled={busy}
                  data-testid="2fa-start-enroll"
                >
                  {busy ? t('twofa.processing') : t('twofa.startEnroll')}
                </Button>
              </div>
            </div>
          ) : null}

          {stage === 'enroll' && enroll ? (
            <Form onSubmit={handleVerify} data-testid="2fa-verify-form">
              {enroll.method === 'TOTP' ? (
                <>
                  <p className="text-sm text-pw-text">
                    {t('twofa.totpScanHint')}
                  </p>
                  {enroll.qrCodeDataUrl ? (
                    <img
                      src={enroll.qrCodeDataUrl}
                      alt={t('twofa.qrCodeAlt')}
                      data-testid="2fa-qr-image"
                      /* dark-ok: QR quiet zone must stay white in both modes to remain scannable */
              className="mx-auto h-48 w-48 rounded-md border border-pw-border bg-white p-2"
                    />
                  ) : null}
                  {enroll.secret ? (
                    <p className="text-center font-mono text-xs text-pw-text-muted">
                      {t('twofa.totpSecretLabel', { secret: enroll.secret })}
                    </p>
                  ) : null}
                </>
              ) : (
                <p className="text-sm text-pw-text" data-testid="2fa-email-hint">
                  {t('twofa.emailSentHintPrefix')} <strong>{enroll.email ?? t('twofa.emailFallback')}</strong>{t('twofa.emailSentHintSuffix')}
                </p>
              )}

              <FormField>
                <FormLabel htmlFor="code">{t('twofa.codeLabel')}</FormLabel>
                <FormControl>
                  <Input
                    id="code"
                    data-testid="2fa-code-input"
                    inputMode="numeric"
                    pattern="\d{6}"
                    maxLength={6}
                    autoFocus
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                  />
                </FormControl>
              </FormField>

              {error ? (
                <FormMessage data-testid="2fa-error">{error}</FormMessage>
              ) : null}

              <div className="flex justify-end gap-2 pt-2">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => {
                    setStage('pick');
                    setEnroll(null);
                    setCode('');
                  }}
                  disabled={busy}
                >
                  {t('twofa.cancel')}
                </Button>
                <Button
                  type="submit"
                  disabled={busy || code.length !== 6}
                  data-testid="2fa-verify-submit"
                >
                  {busy ? t('twofa.verifying') : t('twofa.verifySubmit')}
                </Button>
              </div>
            </Form>
          ) : null}

          {stage === 'recovery' ? (
            <div className="space-y-4" data-testid="2fa-recovery-codes">
              <p className="text-sm text-pw-text">
                <strong>{t('twofa.recoveryTitle')}</strong> {t('twofa.recoveryHint')}
              </p>
              <ul className="grid grid-cols-2 gap-2 rounded-md border border-pw-border bg-pw-surface p-3 font-mono text-xs">
                {recoveryCodes.map((c) => (
                  <li key={c}>{c}</li>
                ))}
              </ul>
              <div className="flex justify-end">
                <Button
                  onClick={() => {
                    setStage('pick');
                    setEnroll(null);
                    setCode('');
                  }}
                >
                  {t('twofa.done')}
                </Button>
              </div>
            </div>
          ) : null}

          {stage === 'disabled' ? (
            <p
              className="text-sm font-medium text-green-600"
              data-testid="2fa-disabled-confirm"
            >
              {t('twofa.disabled')}
            </p>
          ) : null}
        </CardContent>
      </Card>
    </SettingsLayout>
  );
}
